import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  filter,
  map,
  of,
  switchMap,
  withLatestFrom
} from 'rxjs';
import { OccupationCategory } from '@cpd-app/core-module/models';
import { learnerEducationLadderIndex } from '@cpd-app/core-module/models/survey/match-band.util';
import {
  OccupationCardsService,
  OccupationCategoryBusinessRules
} from '@cpd-app/core-module/services/occupation-card';
import { Router } from '@angular/router';
import {
  APP_ROUTES,
  showsPathwaysAndMatches
} from '@cpd-app/core-module/constants/routes';

import { AppState } from '../index';
import { UserProfileSelectors } from '../user-profile';
import { UserProfileActions } from '../user-profile';
import { GoalSelectors } from '../goal';
import { OccupationCardsActions } from '.';

/**
 * The actions that mean "the server has finished recomputing this member's
 * matches", so the cards on screen are now a generation behind the database.
 *
 * The four survey saves are here because each survey service, server side,
 * awaits its own `calculateMatches` AND `createOccupationCardsFromSurvey`
 * before the mutation resolves. By the time the success action is dispatched
 * the new quality ids are already on `api.occupation_card`, so this is the
 * earliest safe point to re-read, not an optimistic guess.
 *
 * `initUserMatchesSuccess` stays for the career-goal path, which still
 * dispatches `initUserMatches` when a member has no pre-goal.
 */
const MATCHES_RECALCULATED: readonly string[] = [
  UserProfileActions.userSkillsSurveySavedSuccess.type,
  UserProfileActions.userInterestsSurveySavedSuccess.type,
  UserProfileActions.userWorkValuesSurveySavedSuccess.type,
  UserProfileActions.userLifestyleSurveySavedSuccess.type,
  UserProfileActions.initUserMatchesSuccess.type
];

@Injectable()
export class OccupationCardsEffects {
  constructor(
    private actions$: Actions,
    private occupationCardsService: OccupationCardsService,
    private store: Store<AppState>,
    private router: Router
  ) {}

  loadOccupationCategories$ = createEffect(() =>
    this.actions$.pipe(
      /*
       * AND AFTER THE MATCHES HAVE BEEN RECALCULATED.
       *
       * `saveUserProfileToState` fires the moment a survey saves, which is
       * before the server has finished recomputing, so the cards it fetches
       * are still a generation behind the answers.
       *
       * This list used to name `initUserMatchesSuccess` as the point the
       * recalculation had landed. That stopped being true the moment the
       * `initUserMatches` dispatch was removed from the survey path, which it
       * had to be, because that call DELETES every UserMatches row and
       * re-seeds them with null quality ids rather than computing anything.
       * Removing it fixed each survey wiping the last, and silently took the
       * card re-read with it: nothing dispatches that action after a survey
       * any more, so the targets simply never filled in.
       *
       * The survey-saved actions are the honest signal. Each survey service
       * awaits its own `calculateMatches` and `createOccupationCardsFromSurvey`
       * before its mutation resolves, so when one of these arrives the new
       * quality ids are already on the card table.
       */
      ofType(
        OccupationCardsActions.loadOccupationCards,
        UserProfileActions.saveUserProfileToState,
        UserProfileActions.userSkillsSurveySavedSuccess,
        UserProfileActions.userInterestsSurveySavedSuccess,
        UserProfileActions.userWorkValuesSurveySavedSuccess,
        UserProfileActions.userLifestyleSurveySavedSuccess,
        UserProfileActions.initUserMatchesSuccess
      ),
      debounceTime(500),
      withLatestFrom(
        this.store.select(UserProfileSelectors.selectHasUserProfileBeenSaved),
        this.store.select(UserProfileSelectors.selectUserProfile),
        this.store.select(GoalSelectors.selectIsCareerGoalSet)
      ),
      filter(
        ([, isUserProfileSaved]) =>
          isUserProfileSaved && showsPathwaysAndMatches(this.router.url)
      ),
      switchMap(([action, , userProfile, isCareerGoalSet]) =>
        this.occupationCardsService
          .getOccupationCardsByCategory(
            undefined,
            /*
             * A recalculation MUST go to the network.
             *
             * `fetch()` defaults to cache-first, and GetOccupationCards takes
             * no arguments that change after a survey, so Apollo answers the
             * re-read out of its own cache with the pre-survey cards and the
             * member watches their targets stay empty. The one case where the
             * cached copy is guaranteed wrong is the case that needs the
             * refetch, so it is the case that bypasses the cache.
             */
            MATCHES_RECALCULATED.includes(action.type)
              ? 'network-only'
              : undefined,
            /*
             * The education term in "Best fit for you", taken from the profile
             * this effect already holds.
             *
             * Computed here rather than injected into the card service, which
             * is a plain query service with no business knowing about the
             * member. Null when nothing is on file, and the ordering is then
             * exactly what it was before.
             */
            learnerEducationLadderIndex(userProfile?.userEducations)
          )
          .pipe(
            map(({ categories, occupationCards }) => {
              const processedCategories: OccupationCategory[] = categories.map(
                (item: OccupationCategory) =>
                  OccupationCategoryBusinessRules.applyBusinessRules(
                    item,
                    userProfile,
                    isCareerGoalSet
                  )
              );

              return OccupationCardsActions.loadOccupationCardsSuccess({
                occupationCategories: processedCategories,
                occupationCards
              });
            }),
            catchError((error) => {
              return of(OccupationCardsActions.loadOccupationCardsError());
            })
          )
      )
    )
  );
}
